import { Inject } from '@odinjs/odin';
import { ContextualBarAPI, DynamicFieldsAPI, FormAPI, Metadata, ModalService, ToastNotificationAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnCancel, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { FetchEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { BrokerService, HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment';
import { FragmentNames, QuestionnaireAliases } from '../../codegen';
import { BS_TASK_LIST_CURRENT_WIZARD_STEP, CTX_KEY_TASK_LIST_FIRST_STEP_RENDERED_NOT_DONE } from '../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { DomainValueType } from '../../constants/domain-value-type';
import TASK_STATUS from '../../constants/task-status';
import TaskItem from '../../interfaces/task-item';
import { cast } from '../../utils/cast-helpers';
import { removeEmptyProperties } from '../../utils/object-utils';
import { TaskModuleUtils } from '../../utils/task-module-utils';
import TasksContext from '../../utils/tasks-context';
import { TasksUtils } from '../../utils/tasks-utils';
import { showToastNotificationError } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import { DYNAMIC_STEP_IDENTIFIER } from '../tasks/tasks-controller';

interface QuestionnaireParams {
  cdMedico?: string;
  ieTipoAgenda?: number,
  nrSeqAgendamento?: number,
  nrSeqAvaliacao: number,
  patientId: number,
}

interface QuestionnaireStoredItem {
  dsResultLong?: string;
  dsResultado: string;
  dsSelect?: string;
  ieResultado: string;
  nrSeqItem: number;
  qtResultado: number;
}

interface CreateSacResearchBody {
  eventType: string;
  nrAtendimento?: number;
  nrSeqAgenda?: number;
  nrSeqCadTarefa: number;
  nrTarefa: number;
  sacResearchList: Record<string, Value>[];
}

interface QuestionnaireSubmitRequest {
  cdMedico?: string;
  ieTipoAgenda?: number;
  nrAtendimento?: number;
  nrSeqAgendamento?: number;
  questionnaireItemsAnswered: Record<string, Value>[];
}

enum ContextKeys {
  QUESTIONNAIRE_TASK = 'questionnaireTask',
}

@Controller({ component: QuestionnaireAliases.QUESTIONNAIRE_LAYOUT, fragment: FragmentNames.QUESTIONNAIRE })
class QuestionnaireController extends BaseController {

  isTasksRoute = false;
  isSatisfactionSurvey = false;

  @Inject(QuestionnaireAliases.QUESTIONNAIRE_DYNAMIC_FIELDS)
    questionnaireDynamicFieldsAPI!: DynamicFieldsAPI;

  @Inject(QuestionnaireAliases.QUESTIONNAIRE_FORM)
    questionnaireFormAPI!: FormAPI;

  @Inject(QuestionnaireAliases.TOAST_NOTIFICATION)
    toastNotificationBlockMessageApi!: ToastNotificationAPI;

  private readonly TASY_QUESTIONNAIRE_DATE_FORMAT = 'DD/MM/YY HH:mm';

  getQuestionnaireParams(): QuestionnaireParams {
    const nrSeqAvaliacao = this.context.get('nrSeqAvaliacao') as number;
    const patientId = UserContext.patientId;

    return {
      nrSeqAvaliacao,
      patientId
    }
  }

  isDateField(field: string): boolean {
    const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

    return regex.test(field);
  }

  @OnMount(QuestionnaireAliases.QUESTIONNAIRE_LAYOUT)
  onLayoutMount(): void {
    this.isTasksRoute = this.historyService.getCurrentRoute() === APPLICATION_ROUTES.TASKS;

    if (this.isTasksRoute) {
      const initalStepIdNotDone = this.context.get(CTX_KEY_TASK_LIST_FIRST_STEP_RENDERED_NOT_DONE);
      this.listenerCurrentWizardStepFromParent(this.questionnaireFormAPI);
      BrokerService.publishToTopic(BS_TASK_LIST_CURRENT_WIZARD_STEP, initalStepIdNotDone);
    }
  }

  private setInitialContextVariablesForTasks(): void {
    const identifier = this.context.get('identifier') as string;
    this.isSatisfactionSurvey = identifier.includes(DYNAMIC_STEP_IDENTIFIER.SATISFACTION_SURVEY);
    const nrSequencia = this.isSatisfactionSurvey ? identifier.split(DYNAMIC_STEP_IDENTIFIER.SATISFACTION_SURVEY.concat('-'))[1] : identifier.split(DYNAMIC_STEP_IDENTIFIER.QUESTIONNAIRE.concat('-'))[1];
    const taskList = this.context.get('taskList') as TaskItem[];
    const questionnaireTask = taskList.find(task => task.nrSequencia === Number(nrSequencia));
    const nrSeqAvaliacao = questionnaireTask?.nrSeqAvaliacao;

    this.context.set({
      nrSeqAvaliacao,
      [ContextKeys.QUESTIONNAIRE_TASK]: questionnaireTask
    });
  }

  @OnMount(QuestionnaireAliases.QUESTIONNAIRE_CONTEXTUAL_BAR)
  onContextualBarMount(event: MountEvent): void {
    const contextualBarApi = event.target as ContextualBarAPI;

    if (this.isSatisfactionSurvey) {
      contextualBarApi.setTitle(MessageService.translate('patient.satisfaction-survey'));
    }
  }

  @OnMount(QuestionnaireAliases.QUESTIONNAIRE_FORM)
  onFormMount(event: MountEvent): void {
    this.isTasksRoute = this.historyService.getCurrentRoute() === APPLICATION_ROUTES.TASKS;

    if (this.isTasksRoute) {
      this.setInitialContextVariablesForTasks();
      const questionnaireTask = this.context.get<TaskItem>(ContextKeys.QUESTIONNAIRE_TASK);
      const formApi = event.target as FormAPI;
      TasksUtils.setInitialFormStatus({
        formApi: formApi,
        onBlocked: () => this.toastNotificationBlockMessageApi.setVisible(true),
        taskItem: questionnaireTask,
      });
    } else {
      this.disableForm(false)
    }
  }

  private disableForm(value: boolean): void{
    this.questionnaireFormAPI.setDisabled(value);
    this.questionnaireFormAPI.setEditing(!value);
  }

  private listenerCurrentWizardStepFromParent(formApi: FormAPI): void {
    const fragmentStepId = this.context.get<string>('identifier');
    const questionnaireTask = this.context.get<TaskItem>(ContextKeys.QUESTIONNAIRE_TASK);
    TaskModuleUtils.listenerCurrentWizardStepFromParent(formApi, fragmentStepId, questionnaireTask);
  }

  @OnAfterFetch(QuestionnaireAliases.QUESTIONNAIRE_FETCH)
  onMountDynamicFields(event: FetchEvent): void {
    this.questionnaireFormAPI.setRecord({});
    this.questionnaireDynamicFieldsAPI.setMetadata(event.data as unknown as Metadata);
    const formCode = this.context.get<number>('nrSeqAvaliacao');
    if (this.isTasksRoute && !this.isSatisfactionSurvey) {
      this.setQuestionnaireFormRecord(APPLICATION_ENDPOINTS.GET_EVALUATION, formCode);
    } else if (this.isTasksRoute && this.isSatisfactionSurvey) {
      this.setQuestionnaireFormRecord(APPLICATION_ENDPOINTS.GET_SATISFACTION_RESEARCH, formCode);
    }
  }

  private setQuestionnaireFormRecord(endpoint: string, formCode: number): void {
    const questionnaireTask = this.context.get<TaskItem>(ContextKeys.QUESTIONNAIRE_TASK);

    if (questionnaireTask.ieStatusTarefa === TASK_STATUS.COMPLETED) {
      const { questionnaireFormAPI } = this;
      HttpService.get(endpoint, { code: formCode, nrSeqAgendamento: questionnaireTask.nrSeqAgenda }).then(res => {
        const questionnaireStoredData = res.body as QuestionnaireStoredItem[];
        questionnaireFormAPI.setRecord(this.getQuestionnaireFormData(questionnaireStoredData));
      })
    }
  }

  private getQuestionnaireFormData(items: QuestionnaireStoredItem[]): Record<string, Value> {

    const formData = {} as Record<string, Value>;
    items.forEach(({ ieResultado, qtResultado, nrSeqItem, dsResultado, dsResultLong, dsSelect }) => {
      switch(ieResultado) {
        case  DomainValueType.BOOLEAN:
          if(qtResultado > 0){
            formData[nrSeqItem] = true;
          }
          break;
        case DomainValueType.SELECT:
        case DomainValueType.SELECT_SIMPLE:
          if(qtResultado > 0){
            formData[nrSeqItem] = { label: dsSelect, value: qtResultado };
          }
          break;
        case DomainValueType.UNIQUE_SELECT:
          formData[nrSeqItem] = { key: dsResultado, value: dsResultado };
          break;
        case DomainValueType.DATE_TIME:
          formData[nrSeqItem] = moment(dsResultado, this.TASY_QUESTIONNAIRE_DATE_FORMAT).format(ISO_DATE_FORMAT);
          break;
        case DomainValueType.SHORT_DESCRIPTION:
          formData[nrSeqItem] = dsResultado;
          break;
        case DomainValueType.LONG_DESCRIPTION:
          formData[nrSeqItem] = dsResultLong || dsResultado;
          break;
        default:
          break;
      }
    });

    return formData;
  }

  private submitQuestionnaireParams(params: Record<string, Value>[], endpoint: string): void {
    const endpointParams = this.getQuestionnaireParams();

    let defaultBodyParams = {};
    if (this.isTasksRoute) {
      defaultBodyParams = {
        cdMedico: TasksContext.cdMedico,
        ieTipoAgenda: TasksContext.ieTipoAgenda,
        nrAtendimento: TasksContext.nrAtendimento,
        nrSeqAgendamento: TasksContext.nrSeqAgenda,
      }
    }

    let bodyParams: CreateSacResearchBody | QuestionnaireSubmitRequest;
    if (endpoint === APPLICATION_ENDPOINTS.POST_SATISFACTION_RESEARCH) {
      if (!TasksContext.nrSeqAgenda && !TasksContext.nrAtendimento) {
        throw Error('nrSeqAgenda or nrAtendimento cannot be null for this process.')
      }

      const questionnaireTask = this.context.get<TaskItem>(ContextKeys.QUESTIONNAIRE_TASK);
      bodyParams = {
        ...defaultBodyParams,
        eventType: TasksContext.eventType,
        nrSeqAgenda: TasksContext.nrSeqAgenda,
        nrSeqCadTarefa: questionnaireTask.nrSeqCadTarefa,
        nrTarefa: questionnaireTask.nrSequencia,
        sacResearchList: params,
      }
    } else {
      bodyParams = {
        ...defaultBodyParams,
        questionnaireItemsAnswered: params,
      }
    }

    HttpService.post(endpoint,
      { ...endpointParams }, { body: removeEmptyProperties(cast(bodyParams)) })
      .then(() => {
        if (this.isTasksRoute || this.isSatisfactionSurvey) {
          const questionnaireTask = this.context.get<TaskItem>(ContextKeys.QUESTIONNAIRE_TASK);
          const nrSequencia = questionnaireTask?.nrSequencia;
          const identifier = this.context.get('identifier') as string;

          HttpService.put(APPLICATION_ENDPOINTS.UPDATE_TASK_STATUS, { nrSequencia }).then(() => {
            BrokerService.publishToTopic(identifier, true);
            this.questionnaireFormAPI.setDisabled(true);
            this.updateTaskStatusOnContext(questionnaireTask);
          });
        } else {
          this.questionnaireFormAPI.setDisabled(true);
          BrokerService.publishToTopic('openConfirmModal', false);
          ModalService.close('patient-questions-pa-modal');
        }
      }).catch(() => {
        showToastNotificationError('newaccount.error.internal.server.error.try.again');
      });
  }

  @OnSubmit(QuestionnaireAliases.QUESTIONNAIRE_FORM)
  onSubmitForm(): void {
    const record = this.questionnaireFormAPI.getRecord();
    const params = [] as Record<string, Value>[];
    Object.keys(record)
      .filter(k => record[k])
      .forEach(k => {
        const value = record[k] as Record<string, Value>;
        switch(true) {
          case typeof value === 'boolean':
            params.push({ dsResultado: '', nrSeqItem: k, qtResultado: record[k] === true ? 1 : 0 });
            break;
          case typeof value === 'object':
            params.push({ dsResultado: value.value, nrSeqItem: k, qtResultado: 0 });
            break;
          case typeof value === 'string' && this.isDateField(record[k] as string):
            params.push({ dsResultado: moment(record[k] as string).format(this.TASY_QUESTIONNAIRE_DATE_FORMAT), nrSeqItem: k, qtResultado: 0 });
            break;
          default:
            params.push({ dsResultado: record[k], nrSeqItem: k, qtResultado: 0 });
            break
        }
      });

    if (this.isSatisfactionSurvey) {
      this.submitQuestionnaireParams(params, APPLICATION_ENDPOINTS.POST_SATISFACTION_RESEARCH);
    } else {
      this.submitQuestionnaireParams(params, APPLICATION_ENDPOINTS.POST_MED_PATIENT_EVALUATION);
    }
  }

  private updateTaskStatusOnContext(questionnaireTask: TaskItem): void {
    questionnaireTask.ieStatusTarefa = TASK_STATUS.COMPLETED;
    this.context.set({ [ContextKeys.QUESTIONNAIRE_TASK]: questionnaireTask });
  }

  @OnCancel(QuestionnaireAliases.QUESTIONNAIRE_FORM)
  onCancelForm(): void {
    if (!this.isTasksRoute) {
      ModalService.close('patient-questions-pa-modal');
    }
  }
}

export default QuestionnaireController;